import {
  isPublicQuestDetail,
  NEARBY_CLIENT_TTL_MS,
  normalizeNearbyQuery,
  OWN_CHANGE_BYPASS_MS,
  questDetailForClientCache,
  QUEST_DETAIL_CLIENT_TTL_MS,
} from "../../shared/quest-cache.ts";
import { type CachedNearby, type CachedQuest, pieceStore } from "../piece-store.ts";
import type { NearbyQuest, QuestDetail } from "./types.ts";

/** The slice of PieceStore (IndexedDB) the quest cache uses. */
export interface QuestCacheStore {
  getQuest(id: string): Promise<CachedQuest | null>;
  putQuest(row: CachedQuest): Promise<void>;
  deleteQuest(id: string): Promise<void>;
  getNearby(key: string): Promise<CachedNearby | null>;
  putNearby(row: CachedNearby): Promise<void>;
  clearNearby(): Promise<void>;
  clearCache(): Promise<void>;
}

type KeyValue = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const BYPASS_KEY = "mapus.quest-cache.bypass-until";

/**
 * Client copies of public quest reads. Published quest details are kept long (immutable)
 * but revalidated whenever they're shown, so a deleted quest still disappears. Nearby
 * lists live a minute. After your own publish/delete, nearby skips this cache and the
 * edge cache for a while, so your change shows at once. Errors are never stored.
 */
export class QuestCache {
  private readonly store: () => QuestCacheStore | null;
  private readonly storage: () => KeyValue | null;
  private readonly now: () => number;

  constructor(opts: { store: () => QuestCacheStore | null; storage: () => KeyValue | null; now?: () => number }) {
    this.store = opts.store;
    this.storage = opts.storage;
    this.now = opts.now ?? Date.now;
  }

  /** True for a while after this browser published or deleted a quest. */
  ownChangeBypass(): boolean {
    try {
      const until = Number(this.storage()?.getItem(BYPASS_KEY) ?? 0);
      return Number.isFinite(until) && until > this.now();
    } catch {
      return false;
    }
  }

  async noteOwnChange(questId: string): Promise<void> {
    try {
      this.storage()?.setItem(BYPASS_KEY, String(this.now() + OWN_CHANGE_BYPASS_MS));
    } catch {
      /* private mode: the edge still refreshes on the next fresh read */
    }
    const store = this.store();
    if (!store) return;
    await Promise.all([store.clearNearby(), store.deleteQuest(questId)]).catch(() => undefined);
  }

  /**
   * Nearby quests for a map view. `load(fresh)` asks the server; `fresh` makes the Worker
   * skip its cache too.
   */
  async nearby(
    query: Record<string, string>,
    load: (fresh: boolean) => Promise<{ quests: NearbyQuest[] }>,
  ): Promise<{ quests: NearbyQuest[] }> {
    const key = normalizeNearbyQuery(query)?.toString() ?? null;
    const store = this.store();
    const fresh = this.ownChangeBypass();
    if (key && store && !fresh) {
      const hit = await store.getNearby(key).catch(() => null);
      if (hit && this.now() - hit.savedMs < NEARBY_CLIENT_TTL_MS && hit.savedMs <= this.now()) {
        return { quests: hit.quests };
      }
    }
    const res = await load(fresh);
    if (key && store) void store.putNearby({ key, quests: res.quests, savedMs: this.now() }).catch(() => undefined);
    return res;
  }

  /** A cached published quest to show right away, or null. Always revalidate with `remember`/`forget`. */
  async cachedQuest(id: string): Promise<QuestDetail | null> {
    const store = this.store();
    const hit = await store?.getQuest(id).catch(() => null);
    if (!hit || this.now() - hit.savedMs > QUEST_DETAIL_CLIENT_TTL_MS) return null;
    if (isPublicQuestDetail(hit.quest)) return hit.quest;
    const migrated = questDetailForClientCache(hit.quest as unknown as Record<string, unknown>);
    if (!migrated) return null;
    const quest = migrated as unknown as QuestDetail;
    void store?.putQuest({ id, quest, savedMs: hit.savedMs }).catch(() => undefined);
    return quest;
  }

  /** Keep a sanitized public copy of any published quest detail (owner/admin API shapes included). */
  async remember(quest: QuestDetail): Promise<void> {
    const stored = questDetailForClientCache(quest as unknown as Record<string, unknown>);
    if (!stored) return;
    await this.store()
      ?.putQuest({ id: quest.id, quest: stored as unknown as QuestDetail, savedMs: this.now() })
      .catch(() => undefined);
  }

  async forget(id: string): Promise<void> {
    await this.store()?.deleteQuest(id).catch(() => undefined);
  }

  async clear(): Promise<void> {
    await this.store()?.clearCache();
  }
}

export const questCache = new QuestCache({
  store: () => (typeof indexedDB === "undefined" ? null : pieceStore),
  storage: () => (typeof localStorage === "undefined" ? null : localStorage),
});
